import "@/App.css";
import "@/lib/keyboard";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Profile from "@/pages/Profile";
import Tags from "@/pages/Tags";
import TagStats from "@/pages/TagStats";
import Settings from "@/pages/Settings";
import BuddyEdit from "@/pages/BuddyEdit";
import Inbox from "@/pages/Inbox";
import Finder from "@/pages/Finder";
import Guide from "@/pages/Guide";
import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminRegistrations from "@/pages/admin/AdminRegistrations";
import AdminCodes from "@/pages/admin/AdminCodes";
import GuestLogin from "@/pages/GuestLogin";
import { ContentAdmin } from "@/components/ContentAdmin";
import { AdminBuddies } from "@/components/AdminBuddies";
import { GuestAdmin } from "@/components/GuestAdmin";
import { ShopAdmin } from "@/components/ShopAdmin";
import { DiscountAdmin } from "@/components/DiscountAdmin";
import { AdminSupport } from "@/components/AdminSupport";
import { SupportIntegrationAdmin } from "@/components/SupportIntegrationAdmin";
import { PremiumAdmin } from "@/components/PremiumAdmin";
import Shop from "@/pages/Shop";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Orders from "@/pages/Orders";
import Activity from "@/pages/Activity";
import ResetPassword from "@/pages/ResetPassword";
import Legal from "@/pages/Legal";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Imprint from "@/pages/Imprint";
import Cookies from "@/pages/Cookies";
import Widerruf from "@/pages/Widerruf";
import { CookieBanner } from "@/components/CookieBanner";
import FamilyJoin from "@/pages/FamilyJoin";
import AppLanding from "@/pages/AppLanding";
import ConfirmEmail from "@/pages/ConfirmEmail";
import Groups from "@/pages/Groups";
import GroupJoin from "@/pages/GroupJoin";
import Support from "@/pages/Support";
import Tickets from "@/pages/Tickets";

function Protected({ children }) {
  const { user, authChecked } = useApp();
  if (!authChecked) {
    return <div className="mx-auto max-w-md h-[100dvh] flex items-center justify-center" style={{ background: "var(--app-bg)" }}><img src="/logo.png" alt="" className="h-14 animate-pulse" /></div>;
  }
  return user ? children : <Navigate to="/" replace />;
}

function AppRouter() {
  const location = useLocation();
  const { user, authChecked } = useApp();

  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/t/:code" element={<Finder />} />
      <Route path="/" element={authChecked && user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/tags" element={<Protected><Tags /></Protected>} />
      <Route path="/tags/:id/edit" element={<Protected><BuddyEdit /></Protected>} />
      <Route path="/tags/:id/stats" element={<Protected><TagStats /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/inbox" element={<Protected><Inbox /></Protected>} />
      <Route path="/guide" element={<Protected><Guide /></Protected>} />
      <Route path="/admin" element={<Protected><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="registrations" element={<AdminRegistrations />} />
        <Route path="content" element={<ContentAdmin />} />
        <Route path="guests" element={<GuestAdmin />} />
        <Route path="buddies" element={<AdminBuddies />} />
        <Route path="codes" element={<AdminCodes />} />
        <Route path="shop" element={<div className="space-y-6"><ShopAdmin /><DiscountAdmin /></div>} />
        <Route path="tickets" element={<div className="space-y-6"><AdminSupport /><SupportIntegrationAdmin /></div>} />
        <Route path="premium" element={<PremiumAdmin />} />
      </Route>
      <Route path="/shop" element={<Protected><Shop /></Protected>} />
      <Route path="/groups" element={<Protected><Groups /></Protected>} />
      <Route path="/support" element={<Protected><Support /></Protected>} />
      <Route path="/tickets" element={<Protected><Tickets /></Protected>} />
      <Route path="/groups/join" element={<Protected><GroupJoin /></Protected>} />
      <Route path="/cart" element={<Protected><Cart /></Protected>} />
      <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
      <Route path="/orders" element={<Protected><Orders /></Protected>} />
      <Route path="/activity" element={<Protected><Activity /></Protected>} />
      <Route path="/login" element={authChecked && user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/legal" element={<Legal />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/imprint" element={<Imprint />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/withdrawal" element={<Widerruf />} />
      <Route path="/family/join" element={<FamilyJoin />} />
      <Route path="/app" element={<AppLanding />} />
      <Route path="/confirm-email" element={<ConfirmEmail />} />
      <Route path="/guest-login" element={<GuestLogin />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <AppRouter />
        <CookieBanner />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AppProvider>
  );
}
